import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useDateStore } from "../stores/date-store";
import { apiGet } from "../lib/api";
import { formatCurrency, formatDateFr } from "../lib/format";
import { SectionHeaderRow, LineRow, ResultRow } from "../components/drilldown-row";

export const Route = createFileRoute("/pnl")({
  component: PnLPage,
});

interface AccountAccount {
  accountCode: string;
  accountLabel: string;
  total: number;
}

interface PnlLineData {
  key: string;
  label: string;
  total: number;
  totalN1: number | null;
  accounts: AccountAccount[];
}

interface PnlSectionData {
  key: string;
  label: string;
  total: number;
  totalN1: number | null;
  lines: PnlLineData[];
}

interface PnlData {
  period: { startDate: string; endDate: string };
  n1Period: { startDate: string; endDate: string } | null;
  sections: {
    produitsExploitation: PnlSectionData;
    chargesExploitation: PnlSectionData;
    resultatExploitation: number;
    resultatExploitationN1: number | null;
    produitsFinanciers: PnlSectionData;
    chargesFinancieres: PnlSectionData;
    resultatFinancier: number;
    resultatFinancierN1: number | null;
    resultatCourantAvantImpots: number;
    resultatCourantAvantImpotsN1: number | null;
    produitsExceptionnels: PnlSectionData;
    chargesExceptionnelles: PnlSectionData;
    resultatExceptionnel: number;
    resultatExceptionnelN1: number | null;
    participationIs: PnlSectionData;
  };
  totalProduits: number;
  totalCharges: number;
  resultatNet: number;
  totalProduitsN1: number | null;
  totalChargesN1: number | null;
  resultatNetN1: number | null;
  unmatched: AccountAccount[];
}

function PnLPage() {
  const { startDate, endDate } = useDateStore();

  const { data, isPending, isFetching, error } = useQuery<PnlData>({
    queryKey: ["pnl", startDate, endDate],
    queryFn: () =>
      apiGet<PnlData>(`/api/reports/pnl?startDate=${startDate}&endDate=${endDate}`),
  });

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Erreur de chargement</div>
      </div>
    );
  }

  const isProfit = data.resultatNet >= 0;

  function renderSection(
    section: PnlSectionData,
    variant: "produits" | "charges",
  ) {
    return (
      <>
        <SectionHeaderRow
          label={section.label.toUpperCase()}
          totalN={section.total}
          totalN1={section.totalN1 ?? undefined}
          variant={variant}
        />
        {section.lines.map((line) => (
          <LineRow
            key={line.key}
            label={line.label}
            totalN={line.total}
            totalN1={line.totalN1 ?? undefined}
            accounts={line.accounts}
            startDate={startDate}
            endDate={endDate}
            balanceDirection={variant === "produits" ? "credit" : "debit"}
          />
        ))}
      </>
    );
  }

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Compte de Résultat</h1>
        <span className="text-sm text-gray-500">
          Du {formatDateFr(startDate)} au {formatDateFr(endDate)}
          {isFetching && " · actualisation…"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SummaryCard
          variant="green"
          label="Produits"
          value={formatCurrency(data.totalProduits)}
        />
        <SummaryCard
          variant="red"
          label="Charges"
          value={formatCurrency(data.totalCharges)}
        />
        <SummaryCard
          variant={isProfit ? "blue" : "orange"}
          label="Résultat net"
          value={formatCurrency(data.resultatNet)}
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                Libellé
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                N ({startDate.slice(0, 4)})
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                {data.n1Period ? `N-1 (${data.n1Period.startDate.slice(0, 4)})` : "N-1"}
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                Var.
              </th>
            </tr>
          </thead>
          <tbody>
            {/* EXPLOITATION */}
            {renderSection(data.sections.produitsExploitation, "produits")}
            {renderSection(data.sections.chargesExploitation, "charges")}
            <ResultRow
              label="RÉSULTAT D'EXPLOITATION"
              totalN={data.sections.resultatExploitation}
              totalN1={data.sections.resultatExploitationN1 ?? undefined}
            />

            {/* FINANCIER */}
            {renderSection(data.sections.produitsFinanciers, "produits")}
            {renderSection(data.sections.chargesFinancieres, "charges")}
            <ResultRow
              label="RÉSULTAT FINANCIER"
              totalN={data.sections.resultatFinancier}
              totalN1={data.sections.resultatFinancierN1 ?? undefined}
            />
            <ResultRow
              label="RÉSULTAT COURANT AVANT IMPÔTS"
              totalN={data.sections.resultatCourantAvantImpots}
              totalN1={data.sections.resultatCourantAvantImpotsN1 ?? undefined}
            />

            {/* EXCEPTIONNEL */}
            {renderSection(data.sections.produitsExceptionnels, "produits")}
            {renderSection(data.sections.chargesExceptionnelles, "charges")}
            <ResultRow
              label="RÉSULTAT EXCEPTIONNEL"
              totalN={data.sections.resultatExceptionnel}
              totalN1={data.sections.resultatExceptionnelN1 ?? undefined}
            />

            {/* PARTICIPATION + IS */}
            {data.sections.participationIs.total !== 0 &&
              renderSection(data.sections.participationIs, "charges")}

            <ResultRow
              label="RÉSULTAT NET"
              totalN={data.resultatNet}
              totalN1={data.resultatNetN1 ?? undefined}
              variant="final"
            />
          </tbody>
        </table>
      </div>

      {data.unmatched.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
          <h3 className="text-sm font-semibold text-yellow-900 mb-2">
            Comptes non mappés ({data.unmatched.length})
          </h3>
          <table className="text-xs">
            <tbody>
              {data.unmatched.map((a) => (
                <tr key={a.accountCode}>
                  <td className="font-mono pr-3 py-0.5">{a.accountCode}</td>
                  <td className="pr-3 py-0.5">{a.accountLabel}</td>
                  <td className="text-right py-0.5">{formatCurrency(a.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const VARIANT_CLASSES = {
  green: { bg: "bg-green-50 border-green-200", label: "text-green-700", value: "text-green-800" },
  red: { bg: "bg-red-50 border-red-200", label: "text-red-700", value: "text-red-800" },
  blue: { bg: "bg-blue-50 border-blue-200", label: "text-blue-700", value: "text-blue-800" },
  orange: { bg: "bg-orange-50 border-orange-200", label: "text-orange-700", value: "text-orange-800" },
} as const;

function SummaryCard({
  variant,
  label,
  value,
}: {
  variant: keyof typeof VARIANT_CLASSES;
  label: string;
  value: string;
}) {
  const v = VARIANT_CLASSES[variant];
  return (
    <div className={`border rounded-lg p-4 ${v.bg}`}>
      <div className={`text-sm font-medium ${v.label}`}>{label}</div>
      <div className={`text-2xl font-bold ${v.value}`}>{value}</div>
    </div>
  );
}
